import React, { ReactNode } from 'react';
import { Box, Button, Flex, Spinner, Text } from 'theme-ui';

export interface DeliverySectionCardProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  actionText?: ReactNode;
  onActionClick?: () => void;
  actionInput?: ReactNode;
  height?: string | number;
  showSaveButton?: boolean;
  isSaveDisabled?: boolean;
  isLoading?: boolean;
  onSave?: () => void;
  sectionBg: string;
  borderColor: string;
  textColor: string;
  subTextColor: string;
  primaryColor: string;
  children: ReactNode;
}

export const DeliveryEmptyState: React.FC<{ icon: ReactNode; text: string; color: string }> = ({ icon, text, color }) => (
  <Flex sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', py: '4px', color, flex: 1 }}>
    {icon}
    <Text sx={{ fontSize: '12px', color }}>{text}</Text>
  </Flex>
);

export const DeliverySectionCard: React.FC<DeliverySectionCardProps> = ({
  title,
  subtitle,
  icon,
  actionText,
  onActionClick,
  actionInput,
  height = '220px',
  showSaveButton = true,
  isSaveDisabled = false,
  isLoading = false,
  onSave,
  sectionBg,
  borderColor,
  textColor,
  subTextColor,
  primaryColor,
  children,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: sectionBg,
        borderRadius: '12px',
        padding: height === 'auto' ? '12px 16px' : '16px',
        border: `1px solid ${borderColor}`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: height === 'auto' ? '80px' : height || '220px',
        height: height || '220px',
        flex: height === 'auto' ? '1 1 auto' : undefined,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {isLoading && (
        <Flex
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'transparent',
            zIndex: 10,
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '12px',
          }}
        >
          <Spinner size={32} color={primaryColor} />
        </Flex>
      )}
      {/* Card Header */}
      <Flex sx={{ justifyContent: 'space-between', alignItems: 'center', mb: '12px' }}>
        <Flex sx={{ alignItems: 'center', gap: '6px' }}>
          {icon}
          <Text sx={{ fontSize: '13px', fontWeight: 600, color: textColor }}>
            {title}{' '}
            {subtitle && (
              <Text as="span" sx={{ fontSize: '11px', color: subTextColor, fontWeight: 400 }}>
                {subtitle}
              </Text>
            )}
          </Text>
        </Flex>
        {typeof actionText === 'string' ? (
          <Text
            onClick={onActionClick}
            sx={{ fontSize: '12px', color: primaryColor, fontWeight: 600, cursor: 'pointer' }}
          >
            {actionText}
          </Text>
        ) : (
          actionText
        )}
      </Flex>

      {/* Action Input (e.g. Add Comment input / Add Link input) */}
      {actionInput}

      {/* Card Content Body */}
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '8px',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '4px',
            height: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(156, 163, 175, 0.4)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(156, 163, 175, 0.7)',
            },
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(156, 163, 175, 0.4) transparent',
        }}
      >
        {children}
      </Flex>

      {/* Save Button Footer */}
      {showSaveButton && onSave && !isSaveDisabled && (
        <Flex sx={{ justifyContent: 'flex-end', mt: 'auto', pt: '8px' }}>
          <Button
            onClick={onSave}
            sx={{
              backgroundColor: primaryColor,
              color: '#FFFFFF',
              borderRadius: '6px',
              padding: '4px 14px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              '&:hover': {
                backgroundColor: '#6634C9',
              },
            }}
          >
            Save
          </Button>
        </Flex>
      )}
    </Box>
  );
};

export default DeliverySectionCard;
